import { css } from '@emotion/react';
import classNames from 'classnames';
import React, { ReactElement } from 'react';
import { button_base_css, button_size_css, getButtonKind, button_size_icon_size_css } from './styles';
import { ButtonSize, ButtonKind, BaseButtonProps } from './types';
import { ButtonIcon } from './ButtonIcon';
import { IconProps } from '../icons/generated';
import { IconLoadingDonuts } from '../icons/generated';
import { Slot, Slottable } from '@radix-ui/react-slot';

export interface ButtonProps extends BaseButtonProps {
  fill?: boolean;
  /** @default 'secondary' */
  kind?: ButtonKind;
  /** @default 'medium' */
  size?: ButtonSize;
  /** @default false */
  loading?: boolean;
  /**
   * 버튼 좌측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 있더라도 kind 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 있더라도 Icon 사이즈는 ButtonSize에 따라 고정됩니다.
   */
  startIcon?: ReactElement<IconProps>;
  /**
   * 버튼 우측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 있더라도 kind 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 있더라도 Icon 사이즈는 ButtonSize에 따라 고정됩니다.
   */
  endIcon?: ReactElement<IconProps>;
}

export interface ButtonIconProps {
  size: ButtonSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      type = 'button',
      size = 'medium',
      kind = 'secondary',
      fill,
      disabled,
      className,
      loading = false,
      startIcon,
      endIcon,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const icon_props = {
      size: button_size_icon_size_css[size],
      color: 'currentColor',
    };
    const loading_indicator = loading && (
      <ButtonIcon {...icon_props}>
        <IconLoadingDonuts size={30} />
      </ButtonIcon>
    );
    const start_icon_node = startIcon && (
      <ButtonIcon {...icon_props}>{loading ? <IconLoadingDonuts size={30} /> : startIcon}</ButtonIcon>
    );
    const end_icon_node = endIcon && (
      <ButtonIcon {...icon_props}>{loading && !startIcon ? <IconLoadingDonuts size={30} /> : endIcon}</ButtonIcon>
    );
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        type={type}
        className={classNames('pds-button', className)}
        css={[
          button_base_css,
          getButtonKind(kind as ButtonKind),
          button_size_css[size],
          css`
            ${fill && 'width: 100%;'}
            cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
          `,
        ]}
        disabled={isDisabled}
        {...props}
        ref={ref}
      >
        {start_icon_node || (!end_icon_node && loading_indicator)}
        <Slottable>{children}</Slottable>
        {end_icon_node}
      </Comp>
    );
  },
);
